import type { ApiClient } from 'grabbitbot-legacy-ui'
import { TRADING_TYPE } from '../config'
import type {
  CoinPrice,
  CoinTransaction,
  DepositableCoin,
  LimitOrder,
  PagedList,
  ServiceAccount,
  SwapQuote,
  Wallet,
  WalletTransaction,
} from './types'

const WALLETS = /^\/wallets/
const OPEN_ORDERS = /^\/order\/spot\/open/
const SERVICE_ACCOUNTS = /^\/serviceAccounts/

/**
 * WnF Exchange API client — port of the legacy `WalletService` and
 * `ServiceAccountService` plus the additive W5 open-order routes. Paths match
 * `api.wnfexchange.com/docs/route-manifest.json` exactly.
 */
export function createWnfClient(client: ApiClient) {
  return {
    // --- wallets (legacy wallet.service.ts) --------------------------------
    searchWallets: () => client.get<Wallet[]>(`/wallets/${TRADING_TYPE}`, { cache: false }),
    viewWallet: (coin: string) =>
      client.get<Wallet>('/wallets/{tradingType}/{coin}', { params: { tradingType: TRADING_TYPE, coin }, cache: false }),
    searchTransactions: (coin: string, pageSize: number, start: string | null) =>
      client.get<PagedList<WalletTransaction>>('/wallets/{tradingType}/{coin}/transactions', {
        params: { tradingType: TRADING_TYPE, coin },
        query: { pageSize, start },
        cache: false,
      }),
    /** Test-funds top-up. `quantity` goes out as a decimal STRING (the
     * backend parses exact Decimal from the text). */
    deposit: (coin: string, quantity: string, reference: string) =>
      client.post<WalletTransaction>(
        '/order/{tradingType}/deposit/{coin}',
        { quantity, reference },
        { params: { tradingType: TRADING_TYPE, coin }, invalidates: [WALLETS] },
      ),
    withdraw: (coin: string, quantity: string, reference: string) =>
      client.post<WalletTransaction>(
        '/order/{tradingType}/withdraw/{coin}',
        { quantity, reference },
        { params: { tradingType: TRADING_TYPE, coin }, invalidates: [WALLETS] },
      ),

    /** Coins that can be deposited / withdrawn, each with its own minimum. */
    searchDepositableCoins: () => client.get<DepositableCoin[]>('/coins/spot/depositable', { cache: false }),

    // --- swap (manual coin conversion, executed as a market order) ----------
    /** `amount` is in units of `from`; 404 `{error:"no_pair"}` when the two
     * coins have no direct pair. */
    quoteSwap: (from: string, to: string, amount: string) =>
      client.get<SwapQuote>('/swap/spot/quote', { query: { from, to, amount }, cache: false }),
    /** Same body as a market-order fill; 409 for wallet errors. */
    swap: (from: string, to: string, amount: string, reference: string) =>
      client.post<CoinTransaction>('/swap/spot', { from, to, amount, reference }, { invalidates: [WALLETS] }),

    // --- orders -------------------------------------------------------------
    searchOpenOrders: () => client.get<LimitOrder[]>('/order/spot/open', { cache: false }),
    cancelOpenOrder: (reference: string) =>
      client.delete<{ order: LimitOrder }>('/order/spot/open/{reference}', {
        params: { reference: encodeURIComponent(reference) },
        invalidates: [OPEN_ORDERS, WALLETS],
      }),
    /** Filled orders of one pair (`GET /order/spot/{coin1}/{coin2}`). */
    searchOrders: (coin1: string, coin2: string) =>
      client.get<CoinTransaction[]>('/order/spot/{coin1}/{coin2}', { params: { coin1, coin2 }, cache: false }),
    /** Anonymous market data — used only to offer the pair list. */
    searchPrices: () => client.get<Record<string, CoinPrice>>('/coins/spot/prices', { auth: false }),

    // --- service accounts (the bot credential) ------------------------------
    searchServiceAccounts: () => client.get<ServiceAccount[]>('/serviceAccounts', { cache: false }),
    /** Answers `201` for a read (legacy parity) — still a 2xx to the client. */
    searchServiceAccountRoles: () => client.get<string[]>('/serviceAccounts/roles', { cache: false }),
    addServiceAccount: (data: { description?: string; comment?: string; roles?: string[] }) =>
      client.post<ServiceAccount>('/serviceAccounts', data, { invalidates: [SERVICE_ACCOUNTS] }),
    /** `GET /serviceAccounts/{id}/pass` — the backend answers the secret as a
     * bare JSON string, or `410` when it can no longer be shown. */
    viewServiceAccountSecret: (id: string) =>
      client.get<string>('/serviceAccounts/{id}/pass', { params: { id }, cache: false }),
    revokeServiceAccount: (id: string) =>
      client.delete<ServiceAccount>('/serviceAccounts/{id}', { params: { id }, invalidates: [SERVICE_ACCOUNTS] }),
  }
}

export type WnfClient = ReturnType<typeof createWnfClient>
